import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/src/renderer/components/ui/alert-dialog'
import { Button } from '@/src/renderer/components/ui/Button'
import { testDataCountTexts, type TestDataRequest } from './useTestDataFlow'

export interface TestDataConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Anlegen, oder Entfernen mit den gezählten Zahlen. Ohne nichts zu zeigen. */
  request: TestDataRequest | null
  onConfirm: () => void
}

/**
 * **Rückfrage vor „Testdaten anlegen / entfernen“** (#269) — Anlegen nennt die Erinnerungen und den Sync,
 * Entfernen die Zahlen und dass auch Eigenes in Testprojekten mitgeht.
 *
 * @example
 * const flow = useTestDataFlow(actions)
 * <TestDataConfirmDialog {...flow.dialog} />
 */
export function TestDataConfirmDialog({ open, onOpenChange, request, onConfirm }: TestDataConfirmDialogProps) {
  const { t } = useTranslation()
  const cancelRef = useRef<HTMLButtonElement>(null)
  if (!request) return null
  const remove = request.action === 'remove'
  /*
    Die Knöpfe sind die `Button`s der App, nicht Radix' `Action`/`Cancel` — wie in
    `ArchiveSectionDialog`: Deren `asChild` gäbe `Button` einen Klick-Handler, den `Button` bei Enter
    ohne Ereignis ruft, Radix liest daraus `defaultPrevented`. Den Anfangsfokus, den Radix sonst auf
    „Abbrechen“ legt, setzen wir selbst. Geschlossen wird über `onConfirm` (`useTestDataFlow`).
  */
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent onOpenAutoFocus={() => cancelRef.current?.focus({ preventScroll: true })}>
        <AlertDialogHeader>
          <AlertDialogTitle>{t(remove ? 'testData.removeTitle' : 'testData.createTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {request.action === 'remove' ? t('testData.removeText', testDataCountTexts(t, request.count)) : t('testData.createText')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button ref={cancelRef} variant="secondary" onClick={() => onOpenChange(false)}>
            {t('testData.cancel')}
          </Button>
          <Button variant={remove ? 'destructive' : 'primary'} onClick={onConfirm}>
            {t(remove ? 'testData.removeConfirm' : 'testData.createConfirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
